import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from 'app/_state/auth/auth-selectors';
import { AuthState } from 'app/_state/auth/auth-state';
import { formDateToISO, isoToDisplayDate } from 'app/events/helpers/timezone-conversion';
import { EventFormState } from 'app/events/helpers/event-form-state';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { GetTimeZones } from 'core/timezones/timezones2';
import { requiredWhen, validationToErrorMessages } from 'core/validators';
import { add, isAfter, isBefore, isSameDay, startOfTomorrow } from 'date-fns';
import {
  AddressDetailsFragment,
  CalendarEventLocationTypeEnum,
  EntityTypeDetailsFragment,
  GetCampaignByIdNoTagsGQL,
  UserRole
} from 'generated/graphql';
import { merge } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { SearchUserQueryUser } from 'shared/components/user-list-autocomplete/user-list-autocomplete.component';
import { P } from '@angular/cdk/keycodes';

const validationMessages = {
  entityTypeId: {
    required: 'Entity type is required.',
  },
  name: {
    required: 'Event name is required.',
  },
  timezone: {
    required: 'Timezone is required.',
  },
  startDate: {
    required: 'Start date is required.',
  },
  startTime: {
    required: 'Start time is required.'
  },
  endDate: {
    matDatepickerMin: 'End date cannot be before start date.'
  },
  endTime: {
    other: 'End time must be after start time'
  },
  locationType: {
    required: 'Location type (in person or virtual) is required.',
  },
  locationNote: {
    required: 'Virtual link/instructions are required.',
  },
  address: {
    // Maybe this should be fixed in address-form-component.ts so I can use "required" here?
    // Also the error tooltip should appear even if the state dropdown isn't touched
    address: 'State is required for in-person location.',
  },
};

@Component({
  selector: 'app-event-form',
  templateUrl: './event-form.component.html',
  styleUrls: ['./event-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventFormComponent
  extends WithDisposableSubject() implements OnInit {
  @Input() calendarEvent: EventFormState;
  @Output() calendarEventUpdated: EventEmitter<EventFormState> = new EventEmitter<EventFormState>();
  @Output() startDateChanged: EventEmitter<Date> = new EventEmitter<Date>();

  clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  timezoneOptions = GetTimeZones();
  assignedUsers: string[] = [];
  requiredUserIds: string[] = [];
  canChangeAssignedUsers = true;
  showEndDateTime = false;
  campaignId: string;
  entityTypeOptions: EntityTypeDetailsFragment[];
  minEndTime = '00:00';

  // If endDate is specified, make sure endTime is after startDate/Time
  endTimeValidator = (control: AbstractControl) => {
    const ef = this.eventForm;
    if (!ef ||
      !ef.value.startDate || !ef.value.startTime ||
      !ef.value.endDate || !ef.value.timeZone) {
      return null;
    }
    if (!control.value) { return { required: true }; }
    const startDate = formDateToISO(ef.value.startDate, ef.value.startTime, ef.value.timeZone);
    const endDate = formDateToISO(ef.value.endDate, control.value, ef.value.timeZone);
    // Timezone doesn't matter here, since we're comparing times in the same timezone
    if (isAfter(startDate, endDate)) {
      return { other: 'End time must be after start time' };
    }
    return null;
  };

  eventForm: FormGroup = new FormGroup({
    entityTypeId: new FormControl<string>('', [Validators.required]),
    name: new FormControl<string>('', [Validators.required]),
    locationType: new FormControl<string>('', [Validators.required]),
    locationNote: new FormControl<string>('', [
      requiredWhen(() => this.eventForm?.value?.locationType === CalendarEventLocationTypeEnum.Virtual),
    ]),
    timeZone: new FormControl<string>(this.clientTimezone, [Validators.required]),
    description: new FormControl<string>(''),
    startDate: new FormControl<number | Date>(null, [Validators.required]),
    startTime: new FormControl<string>(null, [Validators.required]),
    endDate: new FormControl<number | Date>(null),
    endTime: new FormControl<string>(null, [this.endTimeValidator]),
    address: new FormControl<AddressDetailsFragment>(null, [
      requiredWhen(() => this.eventForm?.value?.locationType === CalendarEventLocationTypeEnum.InPerson),
    ]),
  });

  constructor(
    private _cd: ChangeDetectorRef,
    private _getCampaignByIdGQL: GetCampaignByIdNoTagsGQL,
    private _route: ActivatedRoute,
    private _store: Store<AuthState>,
  ) {
    super();
    this.campaignId = this._route.snapshot.queryParams.campaignId;

    // Sets up the form
    this._initForm();
  }

  ngOnInit() {
    if (this.calendarEvent) {
      // Fill form from supplied calendarEvent (from edit or create > multiple events > back)
      const startDate = isoToDisplayDate(this.calendarEvent.startDate, this.calendarEvent.timeZone);
      const { address, endDate, locationNote, ...calendarEvent } = this.calendarEvent;
      this.eventForm.patchValue({
        ...calendarEvent,
        timeZone: this.calendarEvent.timeZone,
        startDate,
        startTime: this.parseTime(startDate),
      });
      if (endDate) {
        const dateObj = isoToDisplayDate(this.calendarEvent.endDate, this.calendarEvent.timeZone);
        this.eventForm.patchValue({
          endDate: dateObj,
          endTime: this.parseTime(dateObj),
        });
      }
      if (address && address.country) { this.eventForm.patchValue({ address }); }
      if (locationNote) { this.eventForm.patchValue({ locationNote }); }
      this.assignedUsers = this.calendarEvent.assignedUsers;
      // Disable changing entityType on edit
      if (this.calendarEvent.id) { this.eventForm.controls.entityTypeId.disable(); }
    }
    // Emit event for initial start date
    this.startDateChanged.emit(this.eventForm.value.startDate);
    // When start date changes, make sure end date (if specified) is not before
    this.eventForm.get('startDate').valueChanges
      .pipe(takeUntil(this.disposer))
      .subscribe((newStart) => {
        // Emit event for new start date
        this.startDateChanged.emit(newStart);
        // If end date is before new start date, set end date to new start date
        if (!this.eventForm.value.endDate) { return; }
        if (isBefore(this.eventForm.value.endDate, newStart)) {
          this.eventForm.patchValue({
            endDate: newStart
          });
        }
      });
  }

  private _initForm() {
    // Update end time validation when start/end date updated

    merge(
      this.eventForm.get('timeZone').valueChanges,
      this.eventForm.get('startDate').valueChanges,
      this.eventForm.get('endDate').valueChanges,
      this.eventForm.get('startTime').valueChanges,
      this.eventForm.get('endTime').valueChanges.pipe(
        distinctUntilChanged()
      ),
    ).pipe(
      takeUntil(this.disposer)
    ).subscribe((value) => {
      // If start and end on same day, endTime must be after startTime

      this.minEndTime = isSameDay(
        this.eventForm.get('startDate').value,
        this.eventForm.get('endDate').value) ?
        this.eventForm.get('startTime').value : '00:00';

      const etc = this.eventForm.get('endTime');
      if (value && etc.value) {
        etc.markAsTouched();
        // For whatever reason the validator is picking up the old value here
        // so hack to delay the check a bit
        setTimeout(() => etc.updateValueAndValidity(), 100);
      }
    });

    // Get entity types and country from campaign
    this._getCampaignByIdGQL
      .fetch({ campaignId: this.campaignId })
      .pipe(map((r) => r.data.getCampaign))
      .subscribe((campaign) => {
        // Default country
        if (!this.eventForm.get('address').value || !this.eventForm.value.address.country) {
          this.eventForm
            .get('address')
            .patchValue({ country: campaign.defaultCountry });
          this._cd.markForCheck();
        }
        // Entity types & default entity type
        this.entityTypeOptions = campaign.entityTypes;
        if (!this.eventForm.get('entityTypeId').value && campaign.defaultEntityType) {
          this.eventForm.patchValue({
            entityTypeId: campaign.defaultEntityType?.id
          });
        }
      });

    // Get current user for organizer default
    this._store.select(
      selectCurrentUser
    ).pipe(
      takeUntil(this.disposer)
    )
      .subscribe(
        (currentUser) => {
          this.requiredUserIds = [currentUser.id];
          if (this.assignedUsers.length === 0) {
            this.assignedUsers = [currentUser.id];
          }
          this._cd.markForCheck();
        }
      );
  }

  // Allow parent to check if event form is valid
  validate(): { isValid: boolean; errorMessages: string[] } {
    const state = this.getCalendarEventFormState(this.eventForm.value);
    this.calendarEventUpdated.emit(
      state
    );
    const errorMessages = validationToErrorMessages(
      this.eventForm,
      validationMessages
    );
    return {
      isValid: this.eventForm.valid && this.assignedUsers.length > 0,
      errorMessages
    };
  }

  getCalendarEventFormState(formData): EventFormState {
    const newValue: EventFormState = {
      name: formData.name,
      entityTypeId: formData.entityTypeId,
      timeZone: formData.timeZone,
      startDate: null,
      endDate: null,
      locationType: formData.locationType,
      description: formData.description,
      campaignId: this.campaignId,
      assignedUsers: this.assignedUsers
    };

    if (formData.startDate && formData.startTime && formData.timeZone) {
      newValue.startDate = formDateToISO(formData.startDate, formData.startTime, formData.timeZone);
    }

    if (formData.endDate && formData.endTime && formData.timeZone) {
      newValue.endDate = formDateToISO(formData.endDate, formData.endTime, formData.timeZone);
    }
    if (formData.locationType === CalendarEventLocationTypeEnum.InPerson && formData.address && formData.address.country) {
      newValue.address = formData.address;
    }
    if (formData.locationType === CalendarEventLocationTypeEnum.Virtual) {
      newValue.locationNote = formData.locationNote;
    }
    return newValue;
  }

  addEndDate() {
    this.showEndDateTime = true;
    const startDate = formDateToISO(this.eventForm.value.startDate, this.eventForm.value.startTime, this.eventForm.value.timeZone);
    const oneHourLater = add(isoToDisplayDate(startDate, this.eventForm.value.timeZone), { hours: 1 });
    this.eventForm.patchValue({
      endDate: oneHourLater,
      endTime: this.parseTime(oneHourLater)
    });
  }

  clearEndDate() {
    this.showEndDateTime = false;
    this.eventForm.get('endDate').reset();
    this.eventForm.get('endTime').reset();
  }

  addEndDateEnabled() {
    return this.eventForm.controls['startDate'].value && this.eventForm.controls['startTime'].value;
  }

  parseTime(date) {
    if (!date) { return; }
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  postFilterUsersAutocompleteFn(user: SearchUserQueryUser) {
    return user.role !== UserRole.Activist;
  }
}
